import { PageLayout } from '@/components/layout/PageLayout';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/shared/SEO';
import { Breadcrumb, useBreadcrumbs } from '@/components/ui/Breadcrumb';
import { usePage } from '@/hooks/usePage';
import { PageSkeleton } from '@/components/ui/Skeleton';
import { Button as RefreshButton } from '@/components/ui/button';
import { SectionPreview } from '@/components/sections/SectionPreview';
import {
  Target,
  Eye,
  Heart,
  Users,
  Home,
  MapPin,
  Calendar,
  Award,
  TrendingUp,
  Shield,
  Lightbulb,
  Trophy,
  HeartHandshake,
  Mail,
  Phone,
  Linkedin,
  Instagram,
  Check,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Mapeo de iconos
const iconMap = {
  'Target': Target,
  'Eye': Eye,
  'Heart': Heart,
  'Users': Users,
  'Home': Home,
  'MapPin': MapPin,
  'Calendar': Calendar,
  'Award': Award,
  'TrendingUp': TrendingUp,
  'Shield': Shield,
  'Lightbulb': Lightbulb,
  'Trophy': Trophy,
  'HeartHandshake': HeartHandshake
};

export default function QuienesSomosPage() {
  const navigate = useNavigate();

  // Cargar datos desde el backend usando usePage
  const { page, viewData, loading, error, refetch } = usePage('quienes-somos');

  /**
   * Render specific section by type with custom logic
   */
  const renderSection = (section: any) => {
    const sectionType = section.type;

    // Skip HERO as it's handled by PageLayout
    if (sectionType === 'HERO') return null;

    // Render specific sections with custom code
    switch (sectionType) {
      case 'INTRO':
        return viewData?.intro?.enabled && (
          <section key={section.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    {viewData.intro.title}
                  </h2>
                  <h3 className="text-xl text-primary mb-6">
                    {viewData.intro.subtitle}
                  </h3>
                  <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                    {viewData.intro.content}
                  </p>

                  <div className="space-y-4">
                    {viewData.intro.highlights?.map((highlight: string, index: number) => (
                      <div key={index} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <img
                    src={viewData.intro.image}
                    alt={viewData.intro.imageAlt}
                    className="rounded-xl shadow-lg w-full h-96 object-cover"
                  />
                </div>
              </div>
            </div>
          </section>
        );

      case 'MISSION':
        return viewData?.mission?.enabled && (
          <section
            key={section.id}
            className="py-16 bg-fixed bg-cover bg-center relative"
            style={{ backgroundImage: `url(${viewData.mission.backgroundImage})` }}
          >
            <div className="absolute inset-0 bg-primary/90"></div>
            <div className="container mx-auto px-4 relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {viewData.mission.title}
                </h2>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {viewData.mission.items?.map((item: any, index: number) => {
                  const IconComponent = iconMap[item.icon as keyof typeof iconMap] || Target;

                  return (
                    <div key={index} className="text-center text-white">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <IconComponent className="h-8 w-8" />
                      </div>
                      <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                      <p className="text-white/90 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      case 'TEAM':
        return viewData?.team?.enabled && (
          <section key={section.id} id="equipo" className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {viewData.team.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {viewData.team.subtitle}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {viewData.team.members
                  ?.filter((member: any) => viewData.team.showAll || member.featured)
                  ?.map((member: any) => (
                  <div key={member.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-64 object-cover"
                    />

                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                      <p className="text-primary font-semibold mb-3">{member.position}</p>
                      <p className="text-gray-600 mb-4">{member.bio}</p>

                      <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                        <span>Experiencia: <strong>{member.experience}</strong></span>
                        <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                          {member.specialization}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex space-x-3">
                          {member.contact?.email && (
                            <a href={`mailto:${member.contact.email}`} className="text-gray-400 hover:text-primary">
                              <Mail className="h-4 w-4" />
                            </a>
                          )}
                          {member.contact?.phone && (
                            <a href={`tel:${member.contact.phone}`} className="text-gray-400 hover:text-primary">
                              <Phone className="h-4 w-4" />
                            </a>
                          )}
                          {member.social?.linkedin && (
                            <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary">
                              <Linkedin className="h-4 w-4" />
                            </a>
                          )}
                          {member.social?.instagram && (
                            <a href={`https://instagram.com/${member.social.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary">
                              <Instagram className="h-4 w-4" />
                            </a>
                          )}
                        </div>

                        <Button size="sm" onClick={() => navigate('/contacto')}>
                          Contactar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {!viewData.team.showAll && viewData.team.members?.length > 3 && (
                <div className="text-center mt-8">
                  <Button variant="outline" onClick={() => navigate('/equipo')}>
                    Ver Todo el Equipo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </section>
        );

      case 'STATS':
        return viewData?.stats?.enabled && (
          <section
            key={section.id}
            className="py-16 bg-fixed bg-cover bg-center relative"
            style={{ backgroundImage: `url(${viewData.stats.backgroundImage})` }}
          >
            <div className="absolute inset-0 bg-black/70"></div>
            <div className="container mx-auto px-4 relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {viewData.stats.title}
                </h2>
                <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                  {viewData.stats.subtitle}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {viewData.stats.items?.map((stat: any, index: number) => {
                  const IconComponent = iconMap[stat.icon as keyof typeof iconMap] || Users;

                  return (
                    <div key={index} className="text-center text-white">
                      <div className="w-16 h-16 bg-primary/80 rounded-full flex items-center justify-center mx-auto mb-4">
                        <IconComponent className="h-8 w-8" />
                      </div>
                      <div className="text-4xl font-bold mb-2">{stat.value}</div>
                      <div className="text-xl font-semibold mb-2">{stat.label}</div>
                      <div className="text-gray-300 text-sm">{stat.description}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      case 'TIMELINE':
        return viewData?.timeline?.enabled && (
          <section key={section.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {viewData.timeline.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {viewData.timeline.subtitle}
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                {viewData.timeline.events?.map((event: any, index: number) => (
                  <div key={index} className="flex items-start mb-8 last:mb-0">
                    <div className="flex-shrink-0 mr-6">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${event.milestone ? 'bg-primary' : 'bg-gray-400'}`}>
                        {event.year.slice(-2)}
                      </div>
                    </div>

                    <div className="flex-grow">
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center mb-2">
                          <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold mr-3">
                            {event.year}
                          </span>
                          {event.milestone && (
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                              Hito Importante
                            </span>
                          )}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                        <p className="text-gray-600">{event.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'CULTURE':
        return viewData?.culture?.enabled && (
          <section
            key={section.id}
            className="py-16 bg-fixed bg-cover bg-center relative"
            style={{ backgroundImage: `url(${viewData.culture.backgroundImage})` }}
          >
            <div className="absolute inset-0 bg-primary/85"></div>
            <div className="container mx-auto px-4 relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {viewData.culture.title}
                </h2>
                <p className="text-xl text-white/90 max-w-3xl mx-auto">
                  {viewData.culture.subtitle}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {viewData.culture.values?.map((value: any, index: number) => {
                  const IconComponent = iconMap[value.icon as keyof typeof iconMap] || Shield;

                  return (
                    <div key={index} className="text-center text-white">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <IconComponent className="h-8 w-8" />
                      </div>
                      <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                      <p className="text-white/90 leading-relaxed">{value.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );

      case 'CERTIFICATIONS':
        return viewData?.certifications?.enabled && (
          <section key={section.id} className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {viewData.certifications.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {viewData.certifications.subtitle}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {viewData.certifications.items?.map((cert: any, index: number) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all">
                    <img
                      src={cert.logo}
                      alt={cert.name}
                      className="w-16 h-16 mx-auto mb-4 rounded-lg object-cover"
                    />
                    <h3 className="font-bold text-gray-900 mb-2">{cert.name}</h3>
                    <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm mb-2">
                      {cert.type}
                    </span>
                    <p className="text-gray-600 text-sm mb-2">{cert.description}</p>
                    <span className="text-gray-500 text-xs">{cert.year}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'CTA':
        return viewData?.cta && (
          <section
            key={section.id}
            className="py-20 bg-fixed bg-cover bg-center relative"
            style={{ backgroundImage: `url(${viewData.cta.backgroundImage})` }}
          >
            <div className="absolute inset-0 bg-primary/80"></div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {viewData.cta.title}
              </h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                {viewData.cta.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-gray-100"
                  onClick={() => navigate(viewData.cta.primaryButton.link)}
                >
                  {viewData.cta.primaryButton.text}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary"
                  onClick={() => navigate(viewData.cta.secondaryButton.link)}
                >
                  {viewData.cta.secondaryButton.text}
                </Button>
              </div>
            </div>
          </section>
        );

      // For any other section type, use SectionPreview
      default:
        return (
          <SectionPreview
            key={section.id}
            section={section}
            isEnabled={section.enabled}
            viewport="desktop"
          />
        );
    }
  };

  const handleHeroAction = () => {
    const ctaLink = viewData?.hero?.ctaLink || '/contacto';
    if (ctaLink.startsWith('#')) {
      // Scroll to section
      const element = document.querySelector(ctaLink);
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(ctaLink);
    }
  };

  // Generate URLs and data
  const currentUrl = `${window.location.origin}/quienes-somos`;

  // JSON-LD Structured Data
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": viewData?.seo?.title?.replace('{tenantName}', 'Tu Inmobiliaria') || "Quiénes Somos | Tu Inmobiliaria",
        "description": viewData?.seo?.description || "Conoce nuestra historia, equipo y valores",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": window.location.origin
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Quiénes Somos",
              "item": currentUrl
            }
          ]
        }
      }
    ]
  };

  // Usar datos dinámicos
  const heroTitle = viewData?.hero?.title || "Quiénes Somos";
  const heroSubtitle = viewData?.hero?.subtitle || "15 años construyendo confianza en el sector inmobiliario";
  const heroImage = viewData?.hero?.backgroundImage || "/images/hero/quienes-somos-hero.jpg";
  const heroShowSearch = viewData?.hero?.showSearch ?? false;
  const heroShowCTA = viewData?.hero?.showCTA ?? false;
  const heroCTAText = viewData?.hero?.ctaText || "Conoce Nuestro Equipo";
  
  // SEO dinámico - Optimizado para SEO
  const seoTitle = viewData?.seo?.title?.replace('{tenantName}', 'Tu Inmobiliaria') || "Quiénes Somos | Tu Inmobiliaria";
  const seoDescription = viewData?.seo?.description || "Conoce nuestra trayectoria en el sector inmobiliario: equipo profesional, valores y compromiso con la excelencia. Tu confianza es nuestro éxito.";
  const seoKeywords = viewData?.seo?.keywords || "quienes somos, historia inmobiliaria, equipo profesional, valores corporativos, trayectoria inmobiliaria, excelencia inmobiliaria";

  // Generate breadcrumb items
  const breadcrumbItems = useBreadcrumbs([
    {
      label: 'Quiénes Somos',
      translationKey: 'nav.about',
    },
  ]);

  if (loading) {
    return <PageSkeleton message="Cargando página..." />;
  }

  if (error && !viewData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Error al cargar la página
          </h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <RefreshButton onClick={refetch}>
            Intentar de nuevo
          </RefreshButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={viewData?.seo?.title?.replace('{tenantName}', '') || "Quiénes Somos"}
        description={seoDescription}
        keywords={seoKeywords?.split(',').map((k: string) => k.trim())}
        canonicalPath="/quienes-somos"
        ogImage={heroImage}
      />

      <Helmet>
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <PageLayout
        hero={{
          title: heroTitle,
          subtitle: heroSubtitle,
          backgroundImage: heroImage,
          showSearch: heroShowSearch
        }}
      >
        {/* Breadcrumb */}
        <div className="container mx-auto px-4 pt-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Render all sections in order */}
        {page?.sections
          ?.filter((section: any) => section.enabled)
          ?.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
          ?.map((section: any) => renderSection(section))}
      </PageLayout>
    </>
  );
}
